import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from "react-router-dom";
import './EWAViewer.scss';
import { extractFilesFromZip, fetchZipFile } from "../../../helpers";
import Context from "../../../context";
import {
  bundleZipJs, getValidFiles,
  htmlPostMessageType,
  injectScriptsAndStyles,
  inlineImports,
  registerMessageHandler
} from "../../../libraries/ewa/helpers";
import PDFViewerInternal from "./PDFViewerInternal";
import appConfig from "../../../config/app";
import { EwaContext } from "../../../context/EwaContext";

const EWAViewer = ({
                     item,
                     mimeType,
                     markAsNavigated,
                     setLoading,
                   }) => {
  const { pathname } = useLocation();

  const [pdfToShow, setPdfToShow] = useState(null);

  const [htmlContent, setHtmlContent] = useState(null);
  const iframeRef = useRef(null);
  const iframeHistoryRef = useRef([]);
  const iframeHistoryIndexRef = useRef(-1);
  const fullyProcessedHtmlFilesRef = useRef({});

  const { itin, setGlobalModalContent, t, viewMode } = useContext(Context);
  const { setScreenNameReady } = useContext(EwaContext);

  const cleanupFunctionsRef = useRef([]);
  const pathToDataUrlMapRef = useRef(null);

  const blobToDataUrl = useCallback((blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  }), []);

  const isRealPdf = async (blob) => {
    const header = await blob.slice(0, 5).text();
    if (!header.startsWith('%PDF-')) return false;

    const footer = await blob.slice(-20).text();
    return footer.includes('%EOF');
  };

  const buildDataUrlMapping = useCallback(
    async (extractedFiles, zip, bundleDataUrl) => {
      const dataUrls = {};

      const validFiles = getValidFiles(extractedFiles);

      let commonFolder = null;
      if (validFiles.length) {
        const firstPath = validFiles[0].path;
        const segments = firstPath.split('/');

        // If there's more than one segment, then there may be a common folder name
        if (segments.length > 1) {
          const candidate = segments[0];
          const allHave = validFiles.every(({ path }) => path.startsWith(candidate + '/'));
          if (allHave) commonFolder = candidate;
        }
      }

      for (const { path, blob } of validFiles) {
        let fileBlob = blob;
        // If the file is a CSS file and its MIME type is missing or is octet-stream,
        // then create a new Blob with type 'text/css'.
        if (path.endsWith('.css') && (!blob.type || blob.type === 'application/octet-stream')) {
          fileBlob = new Blob([blob], { type: 'text/css' });
        }

        if (path.endsWith('.pdf')) {
          const candidateBlob = new Blob([blob]);
          const isValidPdf = await isRealPdf(candidateBlob);
          fileBlob = isValidPdf ? new Blob([blob], { type: 'application/pdf' }) : candidateBlob;
        }

        if (
          (path.endsWith('.js') || path.endsWith('.mjs')) &&
          (!blob.type || blob.type === 'application/octet-stream')
        ) {
          let jsContent = await blob.text();
          let inlinedJsContent = jsContent;
          if (/await\s+import\s*\(\s*['"`]/.test(jsContent) && !bundleDataUrl) {
            inlinedJsContent = await inlineImports(zip, jsContent);
          }

          fileBlob = new Blob([inlinedJsContent], { type: 'application/javascript' });
        }

        if ((path.endsWith('.html') || path.endsWith('.htm')) &&
          (!blob.type || blob.type === 'application/octet-stream')) {
          fileBlob = new Blob([blob], { type: 'text/html' });
        }

        if (path.endsWith('.svg') && (!blob.type || blob.type === 'application/octet-stream')) {
          fileBlob = new Blob([blob], { type: 'image/svg+xml' });
        }

        const url = await blobToDataUrl(fileBlob);
        dataUrls[path] = url;

        let relativePath = path;
        // If a common folder exists and the path starts with that folder, remove it
        if (commonFolder && path.startsWith(commonFolder + '/')) {
          relativePath = path.substring(commonFolder.length + 1);
        }
        // Map the modified relative path to the data URL as well
        dataUrls[relativePath] = url;
      }

      return dataUrls;
    }, [blobToDataUrl]);

  const replaceAssetsAndInternalLinks = useCallback((htmlFilesMap, pathToDataUrlMap) => {
    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const htmlPaths = Object.keys(htmlFilesMap);

    const rewrittenFiles = {};
    const dataUrlToHtmlMap = {};

    for (const [htmlPath, htmlContent] of Object.entries(htmlFilesMap)) {
      let replacedHtml = htmlContent;

      // 1. Replace all asset URLs (images, scripts, css, etc.) with data url
      Object.keys(pathToDataUrlMap).forEach((key) => {
        const cleanedKey = key.replace(/^\.?\//, '');
        const regex = new RegExp(`(?:\\.?/)?${escapeRegex(cleanedKey)}`, 'g');
        replacedHtml = replacedHtml.replace(regex, pathToDataUrlMap[key]);
      });

      // 2. Replace internal HTML links (href=...html) with data url
      htmlPaths.forEach((targetPath) => {
        const dataUrl = pathToDataUrlMap[targetPath];
        if (dataUrl) {
          // Match href="targetPath" or href="./targetPath" or href="some/targetPath"
          const hrefVariants = [targetPath, './' + targetPath, '/' + targetPath];

          hrefVariants.forEach(href => {
            const regex = new RegExp(`href=(["'])${escapeRegex(href)}([?#][^"']*)?\\1`, 'g');
            replacedHtml = replacedHtml.replace(regex, `href="${dataUrl}"`);
          });
        }
      });
      rewrittenFiles[htmlPath] = replacedHtml;

      const dataUrlForThisFile = pathToDataUrlMap[htmlPath];
      if (dataUrlForThisFile) dataUrlToHtmlMap[dataUrlForThisFile] = replacedHtml;
    }

    fullyProcessedHtmlFilesRef.current = dataUrlToHtmlMap;
    return rewrittenFiles;
  }, []);

  const buildBundleFromArchive = useCallback(async (extractedFiles, htmlEntryPath) => {
    const htmlEntryFile = extractedFiles.find(file => file.path === htmlEntryPath);
    if (!htmlEntryFile) return null;

    const htmlSourceText = await htmlEntryFile.blob?.text();
    if (!htmlSourceText) return null;

    // Parse the HTML to locate the <script type="module"> tag
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlSourceText, "text/html");
    const moduleScripts = Array.from(doc.querySelectorAll("script[type='module']"))
      .filter(s => s.src && !s.src.endsWith("install.js"));
    if (!moduleScripts.length) return null;

    const entryPoints = moduleScripts.map(s => s.getAttribute("src"));

    const validFiles = getValidFiles(extractedFiles);

    const scriptFiles = validFiles.filter(({ path }) => [".js", ".mjs", ".cjs"].some(ext => path.endsWith(ext)));
    if (scriptFiles.length <= 1) return null;

    let sharedRootFolder = null;
    const firstPath = scriptFiles[0].path;
    const segments = firstPath.split('/');
    if (segments.length > 1) {
      const candidate = segments[0];
      const allHave = scriptFiles.every(({ path }) => path.startsWith(candidate + '/'));
      if (allHave) sharedRootFolder = candidate;
    }

    const normalizedScriptFiles = scriptFiles.map(file => {
      const clean = file.path.replace(/^\.?\/+/, "");
      if (sharedRootFolder && clean.startsWith(sharedRootFolder + "/")) {
        return { ...file, path: clean.substring(sharedRootFolder.length + 1) };
      }
      return { ...file, path: clean };
    });

    const importRegex = /\bimport\s*(\(|[\w*{])/;
    let hasImports = false;
    for (const ep of entryPoints) {
      const file = normalizedScriptFiles.find(f => f.path === ep);
      if (!file) continue;
      const text = await file.blob.text();
      if (importRegex.test(text)) {
        hasImports = true;
        break;
      }
    }
    if (!hasImports) return null;

    const bundledScriptCode = await bundleZipJs(normalizedScriptFiles, entryPoints);
    if (
      !bundledScriptCode ||
      bundledScriptCode.trim() === "(() => {\n})();\n" ||
      bundledScriptCode.trim() === "(() => {})();"
    ) {
      return null;
    }

    const bundleJsBlob = new Blob([bundledScriptCode], { type: "application/javascript" });
    return await blobToDataUrl(bundleJsBlob);
  }, [blobToDataUrl]);

  useEffect(() => {
    if (mimeType !== 'application/zip') return;
    let isMounted = true;

    const loadContent = async () => {
      if (!isMounted) return;
      setLoading && setLoading(true);
      try {
        const zip = await fetchZipFile(item?.file?.httpsUrl);
        const extractedFiles = await extractFilesFromZip(zip);

        const fallbackFilePath = extractedFiles.find(f => f.path.endsWith('index.html') || f.path.endsWith('index.htm'))?.path;
        const entryPointPath = item?.entryPoint || fallbackFilePath;
        // eslint-disable-next-line no-throw-literal
        if (!entryPointPath) throw { modalMessage: t("no_ewa_entry_point") };

        const htmlFiles = extractedFiles.filter(file => file.path.endsWith('.html') || file.path.endsWith('.htm'));
        const cleanedHtmlFiles = htmlFiles.map(file => {
          // Remove leading `/`, `./`, or `folder/` prefixes
          const cleanedKey = file.path.replace(/^(\.\/|\/)?([^/]+\/)?/, '');
          return {
            ...file,
            path: cleanedKey,
          };
        });

        const bundleDataUrl = await buildBundleFromArchive(extractedFiles, entryPointPath);

        if (!isMounted) return;
        const injectedHtmlFiles = {};
        for (const { path, blob } of cleanedHtmlFiles) {
          const text = await blob.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, 'text/html');
          const storagePrefix = `${itin.id}_${item?.id}_`;

          injectedHtmlFiles[path] = injectScriptsAndStyles(doc, item.trusted, item.id, storagePrefix, bundleDataUrl);
        }

        const pathToDataUrlMap = await buildDataUrlMapping(extractedFiles, zip, bundleDataUrl);
        pathToDataUrlMapRef.current = pathToDataUrlMap;
        const htmlFilesWithDataUrls = replaceAssetsAndInternalLinks(injectedHtmlFiles, pathToDataUrlMap);

        const html = entryPointPath && htmlFilesWithDataUrls[entryPointPath]
          ? htmlFilesWithDataUrls[entryPointPath]
          : Object.values(htmlFilesWithDataUrls)[0] || '';
        if (!isMounted) return;
        setHtmlContent(html);

        iframeHistoryRef.current = [pathToDataUrlMap[entryPointPath]];
        iframeHistoryIndexRef.current = 0;
      } catch (err) {
        setGlobalModalContent(() => ({
          closable: true,
          message: err.modalMessage || err?.message || 'The document is not available',
          buttons: [{
            text: t('ok'),
            onClick: () => {
              setGlobalModalContent(null);
              window.history.back();
            }
          }]
        }));
      } finally {
        setLoading && setLoading(false);
      }
    };

    loadContent();

    return () => {
      isMounted = false;
      fullyProcessedHtmlFilesRef.current = {};
      pathToDataUrlMapRef.current = null;
      iframeHistoryRef.current = [];
      iframeHistoryIndexRef.current = -1;
      setScreenNameReady(true);
    };
  }, [buildBundleFromArchive, itin?.id, setScreenNameReady, replaceAssetsAndInternalLinks, buildDataUrlMapping, mimeType, item, setGlobalModalContent, t, setLoading]);

  useEffect(() => {
    if (!htmlContent) return;
    const cleanup = registerMessageHandler(itin?.id, iframeRef.current, htmlPostMessageType);
    iframeRef.current.dataset.ewaId = item.id;
    cleanupFunctionsRef.current.push(cleanup);
    setScreenNameReady(true);
    return () => {
      cleanupFunctionsRef.current.forEach((cleanupFn) => cleanupFn && cleanupFn());
      cleanupFunctionsRef.current = [];
    };
  }, [setScreenNameReady, iframeRef, item.id, itin?.id, htmlContent])

  useEffect(() => {
    const dataURLtoBlob = (dataURL, overrideMime) => {
      const [header, base64String] = dataURL.split(',');
      const mime = overrideMime || ((header.match(/:(.*?);/) || [])[1] || '');
      const byteString = atob(base64String);
      const byteArray = new Uint8Array(byteString.split('').map(char => char.charCodeAt(0)));
      return new Blob([byteArray], { type: mime });
    };

    const handleMessage = (event) => {
      // Only accept messages from same-origin iframes
      if (event.origin !== "null" && event.origin !== window.location.origin && !event.origin.startsWith("blob:")) return;

      // intercept pdf-click in parent and open pdf in a new tab
      if (event?.data?.type === 'ewa-pdf-click') {
        try {
          if (event?.data?.source === 'assets') {
            const blob = dataURLtoBlob(event.data.url);
            const url = URL.createObjectURL(blob);
            setPdfToShow(url);
            setTimeout(() => URL.revokeObjectURL(url), 10_000);
          } else {
            window.open(event.data.url, '_blank');
          }
        } catch (error) {
          console.error("Error processing PDF click:", error);
        }
      } else if (event?.data?.type === 'ewa-navigation-link-click') {
        // intercept navigation-link-click
        const nextHtml = fullyProcessedHtmlFilesRef.current[event?.data?.path];
        if (fullyProcessedHtmlFilesRef.current?.length > 1 && !nextHtml) return;

        if (nextHtml) setHtmlContent(nextHtml);

        iframeHistoryRef.current = iframeHistoryRef.current.slice(0, iframeHistoryIndexRef.current + 1);
        const lastPath = iframeHistoryRef.current[iframeHistoryIndexRef.current];
        if (event?.data?.path !== lastPath) {
          iframeHistoryRef.current.push(event?.data?.path);
          iframeHistoryIndexRef.current = iframeHistoryRef.current.length - 1;
        }
      } else if (event?.data?.type === 'ewa-fetch-request' && !event?.data?.url.startsWith(appConfig.apiHost)) {
        iframeRef.current?.contentWindow?.postMessage({
          type: "ewa-replace-all-image-urls",
          map: pathToDataUrlMapRef.current,
          delay: 500,
        }, "*");
      } else if (event?.data?.type === 'ewa-refresh-images') {
        iframeRef.current?.contentWindow?.postMessage({
          type: "ewa-replace-all-image-urls",
          map: pathToDataUrlMapRef.current,
          delay: 50,
        }, "*");
      } else if (event?.data?.type === 'ewa-pushstate-navigation') {
        markAsNavigated?.();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [markAsNavigated]);

  const onClosePDFViewerInternal = () => {
    if (pdfToShow) URL.revokeObjectURL(pdfToShow);
    setPdfToShow(null);
  };

  return (
    <>
      <iframe
        ref={iframeRef}
        srcDoc={htmlContent}
        className={`ewa-viewer-container ewa-viewer-container-${viewMode}`}
        title="EWA Viewer"
        {...(!item.trusted ? {
          sandbox: "allow-scripts allow-popups allow-forms allow-modals allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-popups-to-escape-sandbox allow-presentation"
        } : {})}
      />
      {pdfToShow && (
        <PDFViewerInternal pdfUrl={pdfToShow} onClose={onClosePDFViewerInternal} closePath={pathname} />
      )}
    </>
  );
};

export default React.memo(EWAViewer);